import { format } from "date-fns";
import { isEqual, startCase } from "lodash-es";
import {
  computed,
  ref,
  shallowRef,
  watch,
  type ComputedRef,
  type Ref,
} from "vue";

import type { Controller, Document, GraphDatabase } from "@ctrl/controller-rpc";

import { formatRelativeDate } from "@/components/dates";
import { createObject, setGraphProperty } from "@/components/graph/component";
import {
  fromGraphDocument,
  toGraphDocument,
} from "@/components/graph/properties/document-serialization";
import { getGraphQuery, runCompiledQuery } from "@/components/graph/query";
import { isGraphRecordResult, isGraphResult } from "@/components/graph/result";
import { JOURNAL_TABLE_ID } from "@/components/tables";
import type { UseProfile } from "@/composables/useProfile";
import type { BaseLogger } from "@/logger/renderer";
import { bindAsync, releasableRef, releaseBestEffort } from "@/ref";
import { renameRetitledTodoBlocks } from "@/utils/object";

export type EntryRecord = {
  id?: string;
  title: string;
  notes?: Document;
  date: Date;
};

// Stable empty notes object (a fresh object per evaluation would re-trigger
// TitledDocument's useDocumentMerge deep watcher). A journal date without an
// entry is a *loaded* empty document, not "not loaded yet", so this must stay
// a real Document rather than undefined.
const EMPTY_NOTES: Document = { blocks: [] };

const dateToString = (dateValue: Date) =>
  `${format(dateValue, "MMMM d, yyyy")}`;

const formatTitle = (dateValue: Date) =>
  startCase(formatRelativeDate(dateValue));

const createSkeletonEntry = (dateValue: Date): EntryRecord => ({
  title: formatTitle(dateValue),
  notes: EMPTY_NOTES,
  date: dateValue,
});

/**
 * Loads (and keeps live) the journal entry for a given date, and provides the
 * save pipeline for its notes. Shared between the Journal route and the
 * dashboard's journal editor.
 *
 * Owns controller resources (the journal database ref and the live query
 * subscription), freed by the returned `release()`. Await `ready` before
 * relying on `entry` being backed by the live query subscription.
 */
export function useJournalEntry(
  controller: Controller,
  profile: Ref<UseProfile>,
  date: Ref<Date> | ComputedRef<Date>,
  baseLogger: BaseLogger
) {
  const logger = baseLogger.forModule("journal");

  const stringifiedDate = computed(() => dateToString(date.value));

  const skeletonEntry = computed<EntryRecord>(() =>
    createSkeletonEntry(date.value)
  );

  // Resources released by `release()`. Promises so that resources still in
  // flight when release() runs are released once they land.
  const releasables: Array<Promise<{ release: () => unknown }>> = [];
  let released = false;
  const release = () => {
    if (released) return;
    released = true;
    releasables.forEach((promise) =>
      promise.then((resource) => releaseBestEffort(resource))
    );
  };

  const databaseRef = ref<GraphDatabase | null>(null);
  // The profile is resolved before any window mounts its tree (see
  // windows/dashboard/main.ts and App.vue), so the personal workspace id is
  // always concrete here.
  const dbRefPromise = controller.graph.database
    .get(profile.value.user.personal.workspaceId, JOURNAL_TABLE_ID)
    .then((db) => {
      databaseRef.value = db.value;
      return db;
    });
  releasables.push(dbRefPromise);
  const database = computed(() => databaseRef.value);
  const databaseForInject = computed(() => database.value ?? undefined);

  // Create tasks for todo blocks that don't have a task ID yet
  // This is needed because Journal is a custom type and the controller doesn't
  // automatically create tasks like it does for default types (pages, etc.)
  const createTasksForTodoBlocks = async (
    notes: Document,
    entryId: string
  ): Promise<Document> => {
    if (!database.value) return notes;

    const updatedBlocks: typeof notes.blocks = [];
    let hasChanges = false;

    for (const block of notes.blocks) {
      // Check for both falsy and explicit null, AND skip blocks we've already processed
      const isTodo = block.type === "todo";
      const blockTask = block.type === "todo" ? block.task : undefined;
      const hasNoTask = blockTask === null || !blockTask;
      const alreadyProcessed = processedBlockIds.has(block.id);

      if (isTodo && hasNoTask && !alreadyProcessed) {
        // Convert row ID to proper object ID
        const properObjectId = await controller.graph.object.id.make({
          workspace_id: database.value.workspace,
          table_id: database.value.id,
          row_id: entryId,
        });
        // Create a new task for this todo block
        const parentDocument = {
          id: { kind: "object" as const, id: properObjectId },
        };
        const taskId = await controller.task.create(
          { title: block.content || "" },
          { document: parentDocument, notify: false, source: "block" }
        );

        if (taskId) {
          hasChanges = true;
          processedBlockIds.add(block.id);
          updatedBlocks.push({
            ...block,
            task: taskId,
          });
        } else {
          updatedBlocks.push(block);
        }
      } else {
        updatedBlocks.push(block);
      }
    }

    return hasChanges ? { blocks: updatedBlocks } : notes;
  };

  // Flag to prevent infinite loop when updating notes with task IDs
  let isUpdatingFromTaskCreation = false;
  // Track block IDs that we've already created tasks for (to prevent duplicates)
  const processedBlockIds = new Set<string>();

  /**
   * Watch a (reactive) entry's notes and persist changes to the database.
   * Returns the stop handles, matching the shape `useObject` expects.
   */
  const watchEntryNotes = (value: EntryRecord): Array<() => void> => {
    // Notes changes come from TitledDocument's useDocumentMerge sync callback
    return [
      watch(
        () => value?.notes,
        async (notes, previous) => {
          // Skip if we just updated from task creation
          if (isUpdatingFromTaskCreation) {
            isUpdatingFromTaskCreation = false;
            return;
          }
          // Guard against invalid states during navigation/teardown
          if (!value || !notes || isEqual(notes, previous)) {
            return;
          }
          if (!value.id && !value.title) {
            return;
          }

          // Todo blocks renamed in the entry must be mirrored on the tasks
          // behind them: a journal entry is a custom object, whose notes the
          // controller does not reconcile (the same reason tasks are created
          // below). Removed blocks need nothing here: TitledDocument deletes
          // the entities behind the blocks every push drops (see
          // `@/utils/deletion`). This watcher only fires on TitledDocument's
          // debounced sync, so a typing burst costs one rename.
          if (previous) {
            renameRetitledTodoBlocks(
              previous.blocks,
              notes.blocks,
              controller,
              baseLogger
            );
          }

          if (value.id && database.value) {
            // Create tasks for todo blocks without task IDs
            const processedNotes = await createTasksForTodoBlocks(
              notes,
              value.id
            );

            // Update the value if tasks were created (to include task IDs)
            if (processedNotes !== notes) {
              isUpdatingFromTaskCreation = true;
              value.notes = processedNotes;
            }
            setGraphProperty(
              toGraphDocument(processedNotes),
              "Notes",
              value.id,
              database.value,
              controller,
              undefined
            );
          } else if (
            notes.blocks &&
            notes.blocks.length > 0 &&
            database.value
          ) {
            // Create new entry
            createObject(database.value, controller, stringifiedDate.value, {
              Notes: toGraphDocument(notes),
            }).then((newId) => {
              if (newId && value) {
                value.id = newId;
                value.title = formatTitle(date.value);
              }
            });
          }
        },
        { deep: true }
      ),
    ];
  };

  const isLoading = ref(true);

  // Helper function to transform query result into EntryRecord
  const transformQueryResult = (
    resultValue: unknown,
    currentDate: Date,
    titleString: string
  ): EntryRecord => {
    const skeleton = createSkeletonEntry(currentDate);

    if (!(resultValue instanceof Map)) {
      logger.warn("Query did not return a map");
      return skeleton;
    }

    if (resultValue.size === 0) {
      // No entry exists yet for this date - create a skeleton entry
      return skeleton;
    }

    // If, for whatever reason, there are multiple entries with the same title,
    // we take the one with the most blocks (most content)
    if (resultValue.size > 1) {
      logger.warn(
        "Multiple journal entries found for the same date:",
        titleString
      );
    }

    type EntryCandidate = EntryRecord & { id: string; blockCount: number };
    const entries = Array.from(resultValue.entries()).flatMap(
      ([id, data]): EntryCandidate[] => {
        if (
          typeof id !== "string" ||
          !isGraphResult(data) ||
          !isGraphRecordResult(data)
        ) {
          return [];
        }
        const record = data;
        const title =
          typeof record.Title === "string" ? record.Title : titleString;
        const hasNotes =
          typeof record.Notes === "object" &&
          record.Notes != null &&
          !Array.isArray(record.Notes) &&
          !(record.Notes instanceof Map);
        const notes = hasNotes ? fromGraphDocument(record.Notes) : EMPTY_NOTES;
        const blockCount = notes.blocks?.length ?? 0;
        return [{ id, title, notes, date: currentDate, blockCount }];
      }
    );

    if (entries.length === 0) {
      return skeleton;
    }

    const bestEntry = entries.reduce((max, entry) =>
      entry.blockCount > max.blockCount ? entry : max
    );

    return {
      id: bestEntry.id,
      title: formatTitle(currentDate),
      notes: bestEntry.notes,
      date: currentDate,
    };
  };

  // Store the compiled query for reactivity
  const queryRef = ref<Awaited<ReturnType<typeof getGraphQuery>> | null>(null);

  // Keep track of current date context for the query result transformation
  const currentQueryDate = ref<Date>(date.value);
  const currentQueryTitleString = ref<string>(stringifiedDate.value);

  // Live query result from the controller - maintains subscription to data
  // changes. `bindAsync` is called (and its release collected for `release()`)
  // synchronously; the bound ref lands in `queryResult` once ready. The bound
  // ref is boxed in a plain object: nesting it directly in a shallowRef trips
  // Vue's nested-ref unwrapping types.
  const queryResult = shallowRef<{ ref: Readonly<Ref<unknown>> } | null>(null);
  const ready: Promise<void> = (() => {
    const queryResultPromise = bindAsync(queryRef, async (query) => {
      if (!query) {
        return releasableRef(undefined);
      }
      return await runCompiledQuery(query, controller);
    });
    releasables.push(queryResultPromise);
    return queryResultPromise.then((bound) => {
      queryResult.value = { ref: bound };
    });
  })();

  // Computed entry that transforms the live query result
  const entry = computed<EntryRecord>(() => {
    const resultValue = queryResult.value?.ref.value;
    if (!resultValue) {
      return createSkeletonEntry(currentQueryDate.value);
    }
    return transformQueryResult(
      resultValue,
      currentQueryDate.value,
      currentQueryTitleString.value
    );
  });

  // Watch date changes to update the query
  // Watch timestamp (number) instead of Date object to avoid spurious triggers from reference changes
  const stopWatchDate = watch(
    () => date.value.getTime(),
    async () => {
      const currentDate = date.value;
      isLoading.value = true;

      const titleString = dateToString(currentDate);

      // Update context for transformation
      currentQueryDate.value = currentDate;
      currentQueryTitleString.value = titleString;

      try {
        // Wait for the database to load
        const workspace = profile.value.user.personal.workspaceId;
        await dbRefPromise;
        if (!database.value) {
          logger.warn("Journal database not found", { workspace });
          isLoading.value = false;
          queryRef.value = null;
          return;
        }

        // Compile and set the query - bindAsync will handle running it
        const queryString = `${database.value.name} where Title = "${titleString}"`;
        const query = await getGraphQuery(workspace, queryString, controller);

        logger.debug("Journal query loaded", { queryString, ok: !!query });
        queryRef.value = query ?? null;
        isLoading.value = false;
      } catch (err) {
        logger.error("Unable to load journal entry:", err);
        isLoading.value = false;
        queryRef.value = null;
      }
    },
    { immediate: true }
  );
  releasables.push(Promise.resolve({ release: stopWatchDate }));

  // Key to force the entry editor to remount when switching dates.
  // This resets useDocumentMerge's internal state (base, local) to prevent
  // stale data from the previous entry causing infinite merge loops.
  const entryKey = computed(() => stringifiedDate.value);

  return {
    database,
    databaseForInject,
    entry,
    entryKey,
    isLoading,
    ready,
    release,
    skeletonEntry,
    watchEntryNotes,
  };
}
